import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Calendar,
  Clock,
  Edit,
  Link,
  MoreHorizontal,
  Trash2,
  Copy,
  Check,
} from 'lucide-react';
import { EventTypeResponse } from '@/client/types.gen';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getUserProfile } from '@/client/sdk.gen';

interface EventTypeCardProps {
  eventType: EventTypeResponse;
  onEdit: (eventType: EventTypeResponse) => void;
  onDelete: (eventTypeId: string) => void;
  onDuplicate: (eventType: EventTypeResponse) => void;
  onToggleActive: (eventTypeId: string, isActive: boolean) => void;
}

// Format duration in minutes to a readable format
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes
    ? `${hours} hr ${remainingMinutes} min`
    : `${hours} hr`;
};

export function EventTypeCard({
  eventType,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleActive,
}: EventTypeCardProps) {
  const { toast } = useToast();
  const [isActive, setIsActive] = useState(eventType.is_active ?? true);
  const [showCopied, setShowCopied] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    // Fetch the user's profile to get username
    const fetchUsername = async () => {
      try {
        const response = await getUserProfile();
        if (response.data) {
          // Convert name to a username format (lowercase, no spaces)
          const formattedUsername = response.data.name
            .toLowerCase()
            .replace(/\s+/g, '');
          setUsername(formattedUsername);
        }
      } catch (err) {
        console.error('Failed to fetch user profile', err);
      }
    };

    fetchUsername();
  }, []);

  const handleToggleActive = () => {
    const newActiveState = !isActive;
    setIsActive(newActiveState);
    onToggleActive(eventType.id, newActiveState);
  };

  const copyBookingLink = () => {
    const bookingUrl = eventType.event_url ?? '';

    navigator.clipboard
      .writeText(bookingUrl)
      .then(() => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy', err);
      });
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'ONE_ON_ONE':
        return 'One-on-One';
      case 'GROUP':
        return 'Group';
      case 'COLLECTIVE':
        return 'Collective';
      case 'ROUND_ROBIN':
        return 'Round Robin';
      default:
        return category;
    }
  };

  return (
    <Card
      className={`overflow-hidden transition-all duration-200 ${
        !isActive ? 'opacity-70' : ''
      }`}
    >
      <CardHeader className="p-5 bg-gradient-to-r from-primary/5 to-primary/10">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant={isActive ? 'default' : 'outline'}
                    className="py-1"
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Toggle to {isActive ? 'deactivate' : 'activate'} this event
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge variant="secondary">
              {getCategoryLabel(eventType.category ?? 'ONE_ON_ONE')}
            </Badge>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(eventType)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={copyBookingLink}>
                <TooltipProvider>
                  <Tooltip open={showCopied}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center">
                        {showCopied ? (
                          <>
                            <Check className="mr-2 h-4 w-4" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="mr-2 h-4 w-4" />
                            <span>Copy Booking Link</span>
                          </>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copied to clipboard!</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(eventType)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(eventType.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        <h3 className="font-medium text-lg">{eventType.name}</h3>
        {eventType.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {eventType.description}
          </p>
        )}
        <div className="flex flex-col space-y-3 mt-4">
          <div className="flex items-center text-sm text-muted-foreground">
            <Clock className="mr-2 h-4 w-4" />
            {formatDuration(eventType.duration)}
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="mr-2 h-4 w-4" />
            {eventType.is_team_event ? 'Team Event' : 'Individual Event'}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between p-5 bg-muted/20 border-t">
        <div className="flex items-center space-x-2">
          <Switch
            id={`active-${eventType.id}`}
            checked={isActive}
            onCheckedChange={handleToggleActive}
          />
          <Label htmlFor={`active-${eventType.id}`}>
            {isActive ? 'Active' : 'Inactive'}
          </Label>
        </div>
        <Button variant="outline" size="sm" onClick={() => onEdit(eventType)}>
          Settings
        </Button>
      </CardFooter>
    </Card>
  );
}
